import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Chip from "@material-ui/core/Chip";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import EditIcon from "@material-ui/icons/EditOutlined";
import FileCopyIcon from "@material-ui/icons/FileCopyOutlined";
import VpnKeyIcon from "@material-ui/icons/VpnKeyOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { useConfirm } from "../../components/ConfirmDialog";

const useStyles = makeStyles(theme => ({
  page: { padding: theme.spacing(3) },
  title: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 22, marginBottom: theme.spacing(0.5) },
  subtitle: { color: theme.app.textTertiary, marginBottom: theme.spacing(2) },
  head: { display: "flex", alignItems: "center", marginBottom: theme.spacing(2) },
  paper: { boxShadow: "none", border: `1px solid ${theme.app.border}`, borderRadius: 12 },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.app.border}`,
    "&:last-child": { borderBottom: "none" },
  },
  kname: { fontWeight: 600, fontSize: 14 },
  kmask: { fontFamily: "monospace", fontSize: 12.5, color: theme.app.textTertiary },
  scopes: { display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 },
  meta: { fontSize: 12, color: theme.app.textTertiary, width: 120, textAlign: "right" },
  empty: { padding: theme.spacing(5, 2), textAlign: "center", color: theme.app.textTertiary },
  revoked: { opacity: 0.5 },
  groupLabel: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: theme.app.textTertiary, margin: theme.spacing(1.5, 0, 0.5) },
  secretBox: { fontFamily: "monospace", fontSize: 13, background: "#1e1e1e", color: "#d4f7c5", padding: theme.spacing(1.5), borderRadius: 8, wordBreak: "break-all" },
}));

const fmtDate = d => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("pt-BR");
  } catch (e) {
    return "—";
  }
};

const Secrets = () => {
  const classes = useStyles();
  const { confirm } = useConfirm();
  const [keys, setKeys] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [editor, setEditor] = useState(null); // { id?, name, scopes:[] }
  const [reveal, setReveal] = useState(null); // { secret, name } shown once

  const load = useCallback(async () => {
    try {
      const [k, s] = await Promise.all([api.get("/api-keys"), api.get("/api-keys/scopes")]);
      setKeys(k.data.keys || []);
      setCatalog(s.data.scopes || []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const groups = catalog.reduce((acc, s) => {
    (acc[s.group] = acc[s.group] || []).push(s);
    return acc;
  }, {});

  const openNew = () => setEditor({ name: "", scopes: [] });
  const openEdit = k => setEditor({ id: k.id, name: k.name, scopes: k.scopes || [] });

  const toggleScope = key =>
    setEditor(e => ({
      ...e,
      scopes: e.scopes.includes(key) ? e.scopes.filter(s => s !== key) : [...e.scopes, key],
    }));

  const save = async () => {
    if (!editor.name.trim()) return;
    try {
      if (editor.id) {
        await api.put(`/api-keys/${editor.id}`, { name: editor.name.trim(), scopes: editor.scopes });
        setEditor(null);
        load();
      } else {
        const { data } = await api.post("/api-keys", { name: editor.name.trim(), scopes: editor.scopes });
        setEditor(null);
        setReveal({ secret: data.secret, name: data.key.name });
        load();
      }
    } catch (err) {
      toastError(err);
    }
  };

  const regenerate = async k => {
    if (!(await confirm({ title: "Regenerar chave", message: `Gerar um novo secret para "${k.name}"? O atual deixa de funcionar imediatamente.`, confirmText: "Regenerar", danger: true }))) return;
    try {
      const { data } = await api.post(`/api-keys/${k.id}/regenerate`);
      setReveal({ secret: data.secret, name: data.key.name });
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const revoke = async k => {
    if (!(await confirm({ title: "Excluir chave", message: `Excluir "${k.name}"? As integrações que a usam param de funcionar.`, confirmText: "Excluir", danger: true }))) return;
    try {
      await api.delete(`/api-keys/${k.id}?hard=1`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const copy = txt => {
    try {
      navigator.clipboard.writeText(txt);
      toast.success("Copiado!");
    } catch (e) {
      /* clipboard blocked */
    }
  };

  const labelOf = key => {
    const f = catalog.find(s => s.key === key);
    return f ? f.label : key;
  };

  return (
    <div className={classes.page}>
      <div className={classes.head}>
        <div style={{ flex: 1 }}>
          <Typography variant="h5" className={classes.title}>Secrets</Typography>
          <Typography className={classes.subtitle}>
            Chaves de API para usar o numera por código (REST <code>/v1</code>). O secret é exibido <b>uma única vez</b> na criação — guarde com segurança.
          </Typography>
        </div>
        <Button variant="contained" color="primary" startIcon={<VpnKeyIcon />} onClick={openNew}>
          Nova chave
        </Button>
      </div>

      <Paper className={classes.paper}>
        {keys.length === 0 ? (
          <div className={classes.empty}>Nenhuma chave ainda. Crie a primeira para acessar a API.</div>
        ) : (
          keys.map(k => (
            <div key={k.id} className={`${classes.row} ${k.revokedAt ? classes.revoked : ""}`}>
              <VpnKeyIcon fontSize="small" style={{ color: "#9a9a9a" }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className={classes.kname}>
                  {k.name}
                  {k.revokedAt && <Chip size="small" label="revogada" style={{ marginLeft: 8, height: 18 }} />}
                  {k.mustRotate && <Chip size="small" label="regenerar" color="secondary" style={{ marginLeft: 8, height: 18 }} />}
                </div>
                <div className={classes.kmask}>{k.masked}</div>
                <div className={classes.scopes}>
                  {(k.scopes || []).length === 0 ? (
                    <span style={{ fontSize: 12, color: "#b00" }}>sem permissões</span>
                  ) : (
                    k.scopes.map(s => <Chip key={s} size="small" label={labelOf(s)} style={{ height: 20 }} />)
                  )}
                </div>
              </div>
              <span className={classes.meta}>último uso<br />{fmtDate(k.lastUsedAt)}</span>
              <IconButton size="small" title="Editar permissões" onClick={() => openEdit(k)}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" title="Regenerar secret" onClick={() => regenerate(k)}>
                <RefreshIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" title="Excluir" onClick={() => revoke(k)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </div>
          ))
        )}
      </Paper>

      {/* criar / editar */}
      <Dialog open={!!editor} onClose={() => setEditor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editor && editor.id ? "Editar chave" : "Nova chave de API"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            size="small"
            label="Nome (ex.: integração-site)"
            value={editor ? editor.name : ""}
            onChange={e => {
              // React 16 event pooling: capture value BEFORE the functional
              // updater runs, senão e.target vira null e dá tela branca.
              const v = e.target.value;
              setEditor(s => ({ ...s, name: v }));
            }}
          />
          {Object.keys(groups).map(g => (
            <div key={g}>
              <div className={classes.groupLabel}>{g}</div>
              {groups[g].map(s => (
                <FormControlLabel
                  key={s.key}
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={!!editor && editor.scopes.includes(s.key)}
                      onChange={() => toggleScope(s.key)}
                    />
                  }
                  label={s.label}
                  style={{ display: "block", marginLeft: 0 }}
                />
              ))}
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditor(null)}>Cancelar</Button>
          <Button onClick={save} variant="contained" color="primary" disabled={!editor || !editor.name.trim()}>
            {editor && editor.id ? "Salvar" : "Criar e mostrar secret"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* secret exibido uma única vez */}
      <Dialog open={!!reveal} onClose={() => setReveal(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Guarde seu secret</DialogTitle>
        <DialogContent>
          <Typography style={{ marginBottom: 12, fontSize: 14 }}>
            Esta é a única vez que mostramos o secret de <b>{reveal && reveal.name}</b>. Copie e guarde com segurança — não dá pra ver de novo.
          </Typography>
          <div className={classes.secretBox}>{reveal && reveal.secret}</div>
          <Button startIcon={<FileCopyIcon />} onClick={() => copy(reveal.secret)} style={{ marginTop: 12 }}>
            Copiar
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReveal(null)} variant="contained" color="secondary">
            Guardei, fechar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Secrets;
